import { parseCsvLine, splitCsvRecords, stripBom } from '@/lib/csv';

/**
 * Pure CSV parsing, lifted out of `useCsvParser` so it can be unit-tested
 * without a renderer and reused by the count-sheet round-trip test.
 *
 * `useCsvParser` is now a thin wrapper over this. Every new option is OPT-IN
 * and default-off, so all five existing consumers parse byte-identically.
 */

export type RawCsvRow = Record<string, string | number>;

/**
 * Structured, translatable parse errors. The pure layer cannot call `t()`, so
 * it emits codes + context and the component boundary resolves them to i18n
 * keys. Without this every new parser error would be permanently English.
 */
export type CsvParseError =
  | { code: 'empty_file' }
  | { code: 'no_header' }
  | { code: 'missing_column'; column: string; foundHeaders: string[] }
  | { code: 'duplicate_column'; first: string; second: string; canonical: string }
  | { code: 'rejected_header'; header: string; reason: string }
  | { code: 'field_count'; line: number; expected: number; got: number }
  | { code: 'unterminated_quote' }
  | { code: 'row_invalid'; line: number; message: string };

export interface CsvParseOptions<T> {
  requiredColumns: string[];
  /**
   * Alias map (folded header -> canonical name). OPT-IN and stocktake-only.
   *
   * ⚠ Must never be applied globally: `usePurchaseOrderCsvParser` reads
   * `row.order_quantity`. Unmapped headers
   * keep their exact lowercased form, underscores intact — only the ALIAS
   * LOOKUP folds `_` to a space, never the emitted key.
   */
  columnAliases?: Record<string, string>;
  /**
   * Canonical column names to parse strictly as integers: blank or junk yields
   * NaN, never 0. Takes precedence over the legacy substring sniff below.
   */
  numericColumns?: string[];
  /** Headers that must cause the file to be rejected (e.g. a count sheet in Add Stock). */
  rejectHeaders?: Array<{ header: string; reason: string }>;
  /** Pad trailing missing cells. Safe only when every machine-read column is leftmost. */
  padShortRows?: boolean;
  /** Silently drop `,,,,,,` filler records instead of failing the whole file. */
  skipBlankRows?: boolean;
  validateRow?: (row: RawCsvRow, index: number) => { valid: boolean; error?: string };
  transformRow?: (row: RawCsvRow, index: number) => T;
}

export interface CsvParseResult<T> {
  data: T[];
  /** Human-readable errors, kept for byte-compatibility with existing consumers. */
  errors: string[];
  /** The same errors as translatable codes. */
  structuredErrors: CsvParseError[];
  /** 1-based source line for `data[i]`. Survives blank-skipping and padding. */
  sourceRows: number[];
  /** Original cell text for `data[i]`, so callers can tell blank from junk. */
  rawRows: Record<string, string>[];
  rowCount: number;
}

/** Lowercase + trim + BOM-strip. Underscores are PRESERVED. */
const cleanHeader = (raw: string): string => stripBom(raw).trim().toLowerCase();

/** Alias-lookup key only: collapse runs of `_` and whitespace to one space. */
const foldHeader = (cleaned: string): string => cleaned.replace(/[_\s]+/g, ' ').trim();

export interface CanonicalizeHeadersResult {
  headers: string[];
  errors: CsvParseError[];
}

/**
 * Map raw headers to canonical names via an opt-in alias table.
 *
 * Two headers resolving to the SAME canonical name is a HARD ERROR naming both:
 * this feeds a stock-SETTING import, and silently picking one of two
 * contradictory stock columns can zero a warehouse.
 */
export const canonicalizeCsvHeaders = (
  rawHeaders: string[],
  aliases: Record<string, string> = {},
): CanonicalizeHeadersResult => {
  const errors: CsvParseError[] = [];
  const seen = new Map<string, string>();

  const headers = rawHeaders.map((raw) => {
    const cleaned = cleanHeader(raw);
    const canonical = aliases[cleaned] ?? aliases[foldHeader(cleaned)] ?? cleaned;

    const previous = seen.get(canonical);
    if (previous !== undefined) {
      errors.push({ code: 'duplicate_column', first: previous, second: raw.trim(), canonical });
    } else {
      seen.set(canonical, raw.trim());
    }
    return canonical;
  });

  return { headers, errors };
};

/**
 * Strict integer parse for a stock cell.
 *
 * - blank  -> NaN (caller distinguishes "not counted" via `rawRows`)
 * - junk   -> NaN (never 0 — the legacy `isNaN ? 0` coercion silently zeroed SKUs)
 * - `'-5`  -> -5, so legacy files carrying the old apostrophe-guarded export
 *            hit the loud "cannot be negative" error instead of becoming 0.
 * - `1,500` -> 1500, but `1,5` is REJECTED: in a European locale that is the
 *            decimal 1.5, and blanket comma-stripping would write 15.
 */
export const parseStrictInt = (raw: string): number => {
  const trimmed = raw.trim();
  if (trimmed === '') return NaN;

  let s = trimmed.startsWith("'") ? trimmed.slice(1).trim() : trimmed;
  if (/^-?\d{1,3}(,\d{3})+$/.test(s)) s = s.replace(/,/g, '');
  if (!/^-?\d+$/.test(s)) return NaN;

  return Number(s);
};

const LEGACY_NUMERIC_SNIFF = (header: string): boolean =>
  header.includes('stock') ||
  header.includes('quantity') ||
  header === 'notify' ||
  header === 'current_stock';

export function parseCsvText<T = RawCsvRow>(
  text: string,
  options: CsvParseOptions<T>,
): CsvParseResult<T> {
  const {
    requiredColumns,
    columnAliases,
    numericColumns,
    rejectHeaders,
    padShortRows,
    skipBlankRows,
    validateRow,
    transformRow,
  } = options;

  const structuredErrors: CsvParseError[] = [];
  const data: T[] = [];
  const sourceRows: number[] = [];
  const rawRows: Record<string, string>[] = [];

  const fail = (): CsvParseResult<T> => ({
    data: [],
    errors: structuredErrors.map(formatCsvParseError),
    structuredErrors,
    sourceRows: [],
    rawRows: [],
    rowCount: 0,
  });

  const { records, unterminatedQuote } = splitCsvRecords(text);

  if (unterminatedQuote) structuredErrors.push({ code: 'unterminated_quote' });

  if (records.length === 0) {
    structuredErrors.push({ code: 'empty_file' });
    return fail();
  }

  const headerLine = records[0];
  if (!headerLine) {
    structuredErrors.push({ code: 'no_header' });
    return fail();
  }

  const rawHeaderCells = parseCsvLine(headerLine);
  const { headers, errors: headerErrors } = canonicalizeCsvHeaders(
    rawHeaderCells,
    columnAliases ?? {},
  );
  structuredErrors.push(...headerErrors);

  for (const rejected of rejectHeaders ?? []) {
    if (headers.includes(rejected.header)) {
      structuredErrors.push({
        code: 'rejected_header',
        header: rejected.header,
        reason: rejected.reason,
      });
    }
  }

  for (const column of requiredColumns) {
    if (!headers.includes(column.toLowerCase())) {
      structuredErrors.push({ code: 'missing_column', column, foundHeaders: headers });
    }
  }

  if (structuredErrors.length > 0) return fail();

  const numericSet = new Set(numericColumns ?? []);

  for (let i = 1; i < records.length; i++) {
    const line = records[i]?.trim();
    if (!line) continue;

    const values = parseCsvLine(line);

    if (skipBlankRows && values.every((v) => v.trim() === '')) continue;

    if (values.length < headers.length && padShortRows) {
      while (values.length < headers.length) values.push('');
    }

    if (values.length !== headers.length) {
      structuredErrors.push({
        code: 'field_count',
        line: i + 1,
        expected: headers.length,
        got: values.length,
      });
      continue;
    }

    const row: RawCsvRow = {};
    const rawRow: Record<string, string> = {};

    headers.forEach((header, index) => {
      const value = values[index]?.trim() ?? '';
      rawRow[header] = value;

      if (numericSet.has(header)) {
        // Strict: blank/junk -> NaN, never 0.
        row[header] = parseStrictInt(value);
      } else if (LEGACY_NUMERIC_SNIFF(header)) {
        // Untouched legacy behaviour for every parser that did not opt in.
        const num = parseInt(value, 10);
        row[header] = isNaN(num) ? 0 : num;
      } else {
        row[header] = value;
      }
    });

    if (validateRow) {
      const validation = validateRow(row, i);
      if (!validation.valid) {
        structuredErrors.push({
          code: 'row_invalid',
          line: i + 1,
          message: validation.error ?? 'Invalid row',
        });
        continue;
      }
    }

    data.push(transformRow ? transformRow(row, i) : (row as unknown as T));
    sourceRows.push(i + 1);
    rawRows.push(rawRow);
  }

  return {
    data,
    errors: structuredErrors.map(formatCsvParseError),
    structuredErrors,
    sourceRows,
    rawRows,
    rowCount: records.length - 1,
  };
}

/** English fallback rendering. UI surfaces should translate the code instead. */
export function formatCsvParseError(error: CsvParseError): string {
  switch (error.code) {
    case 'empty_file':
      return 'CSV file is empty';
    case 'no_header':
      return 'CSV file has no header row';
    case 'missing_column':
      return `Missing required column: ${error.column} (file has: ${error.foundHeaders.join(', ')})`;
    case 'duplicate_column':
      return `Duplicate column: "${error.first}" and "${error.second}" both mean "${error.canonical}". Remove one and re-upload.`;
    case 'rejected_header':
      return error.reason;
    case 'field_count':
      return `Line ${error.line}: expected ${error.expected} columns, got ${error.got}. Check for an unescaped comma or quote.`;
    case 'unterminated_quote':
      return 'CSV has an unclosed quote. Check for a stray " character.';
    case 'row_invalid':
      return `Line ${error.line}: ${error.message}`;
  }
}
